import { BrandMark } from "../brand-mark";
import { useEffect, useRef, useState } from "react";
import {
  Check,
  Frame,
  Layers,
  Monitor,
  MousePointer2,
  Plus,
  Smartphone,
  Type,
  Undo2,
} from "lucide-react";
import { starterDesign } from "../../lib/seed";
import { ElementVisual } from "../editor/element-visual";

// Stable IDs make the same sample render on the server and in the browser.
const source = starterDesign("marketing-preview").elements;
const ids = new Map(
  source.map((element, index) => [element.id, `sample-${index}`]),
);
const sample = source.map((element) => ({
  ...element,
  id: ids.get(element.id)!,
  parentId: element.parentId ? ids.get(element.parentId)! : null,
}));

export function CanvasDemo() {
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [edited, setEdited] = useState(false);
  const [area, setArea] = useState({ width: 900, height: 470 });
  const viewport = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setArea({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }),
    );
    if (viewport.current) observer.observe(viewport.current);
    return () => observer.disconnect();
  }, []);
  const elements = sample.map((element) =>
    edited && ["Greeting", "Mobile greeting"].includes(element.name)
      ? {
          ...element,
          text: "Make space for your next idea.",
          fontSize: element.name === "Greeting" ? 28 : 23,
        }
      : element,
  );
  const frame = elements.filter((element) => !element.parentId)[
    device === "desktop" ? 0 : 1
  ];
  const scale = Math.min(
    (area.width - 48) / frame.width,
    (area.height - 50) / frame.height,
    1,
  );
  return (
    <div className="mk-demo">
      <div className="mk-demo-toolbar">
        <div>
          <BrandMark className="mk-demo-mark" />
          <strong>Orbit workspace</strong>
          <span className="mk-demo-sample">Sample design</span>
        </div>
        <div className="mk-device-toggle" aria-label="Preview size">
          <button
            onClick={() => setDevice("desktop")}
            aria-pressed={device === "desktop"}
            aria-label="Desktop preview"
          >
            <Monitor size={15} />
            <span>Desktop</span>
          </button>
          <button
            onClick={() => setDevice("mobile")}
            aria-pressed={device === "mobile"}
            aria-label="Mobile preview"
          >
            <Smartphone size={15} />
            <span>Mobile</span>
          </button>
        </div>
      </div>
      <div
        className={`mk-demo-body ${device === "mobile" ? "mk-demo-mobile" : ""}`}
      >
        <div className="mk-demo-layers" aria-hidden="true">
          <strong>
            <Layers size={13} /> Layers
          </strong>
          <span className="is-selected">
            <Frame size={13} /> Orbit ·{" "}
            {device === "desktop" ? "Desktop" : "Mobile"}
          </span>
          {["Navigation", "Overview", "Project cards", "Your projects"].map(
            (name) => (
              <span key={name}>
                <Frame size={11} />
                {name}
              </span>
            ),
          )}
          <span>
            <Type size={12} /> {edited ? "Updated heading" : "Heading"}
          </span>
          <div className="mk-demo-layer-footer">
            <MousePointer2 size={13} /> Everything is editable
          </div>
        </div>
        <div className="mk-demo-canvas" ref={viewport}>
          <div
            style={{ width: frame.width * scale, height: frame.height * scale }}
          >
            <div
              style={{
                width: frame.width,
                height: frame.height,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "relative",
              }}
            >
              <ElementVisual
                element={{ ...frame, x: 0, y: 0 }}
                elements={elements}
                selected={null}
                onSelect={() => {}}
                preview
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mk-demo-caption">
        <span role="status">
          {edited ? (
            <>
              <Check size={14} /> Example edit applied to the heading
            </>
          ) : (
            <>
              <span className="mk-status-dot" /> Interactive preview · try an
              example edit
            </>
          )}
        </span>
        <button onClick={() => setEdited(!edited)}>
          {edited ? <Undo2 size={14} /> : <Plus size={14} />}
          {edited ? "Reset preview" : "Try an agent edit"}
        </button>
      </div>
    </div>
  );
}
